import { BookingPageTagName } from '../../../../domain/entities/BookingPage';
import * as ICustomer from '../../../customer';
import * as IBookings from '../../bookings';

export interface Image {
  thumb?: {
    url: string;
  };
  small?: {
    url: string;
  };
  medium?: {
    url: string;
  };
}

export interface MerchantProductData {
  public_id: string;
  scheme: number;
  name: string;
  description?: string;
  price: number;
  image: Image;
  ticket_count?: number;
  expiration?: number;
  booking_pages: IBookings.MerchantBookingData[];
}

export enum FinishedStatus {
  Accepted = 'accepted',
  Pending = 'pending',
  Failed = 'failed',
}

export interface BookingPage {
  publicId: string;
  name: string;
  tagNames: BookingPageTagName[];
  status: string;
  prepaymentEnabled: boolean;
  onsitePaymentEnabled: boolean;
  waitingListProvided: boolean;
  descriptionText?: string;
  primaryImage?: IBookings.MerchantBookingPrimaryImage;
}

export enum EntireErrorType {
  Unexpected = 'unexpected',
  CustomerFailed = 'customerFailed',
  PaymentFailed = 'paymentFailed',
}

export interface SettingCompletion {
  bookingPages: BookingPage[];
  reservationCount: ReservationCount;
  customerPublicId?: string;
  verifiedUser?: boolean;
  expiryDate?: string;
  entireErrors?: EntireErrorType[];
}

export type ReservableType =
  | ReservableTypeMonthly
  | ReservableTypeFixedDays
  | ReservableTypeFixedHourse
  | undefined;

interface ReservableTypeMonthly {
  type: 'monthly';
  months: number;
  days: number;
  hours: number;
  minutes: number;
}

interface ReservableTypeFixedDays {
  type: 'fixed_term_x_days';
  days: number;
}

interface ReservableTypeFixedHourse {
  type: 'fixed_term_x_hours';
  hours: number;
}

export type ChangeableType =
  | ChangeableTypeAccept
  | ChangeableTypeNotAccept
  | undefined;

interface ChangeableTypeAccept {
  type: 'accept';
  until: number; // sec
}

interface ChangeableTypeNotAccept {
  type: 'not_accept';
}

export type CancelableType =
  | CancelableTypeAccept
  | CancelableTypeNotAccept
  | undefined;

interface CancelableTypeAccept {
  type: 'accept';
  until: number; // sec
  prepayable: boolean;
  refundableDaysLimit: number;
}

interface CancelableTypeNotAccept {
  type: 'not_accept';
}

export interface ReservationCount {
  upcoming?: number;
  perMonth?: number;
  perWeek?: number;
  perDay?: number;
}

export interface SettingConfirmation {
  reservationCount: ReservationCount;
  expiryDate?: string;
}

export enum PaymentSelection {
  UsedCreditCard = 'used_credit_card',
  AnotherCreditCard = 'another_credit_card',
}

export interface CreditCard {
  cardNumber?: string;
  securityCode?: string;
  year?: string;
  month?: string;
}

export type CandidateViewType = 'calendar' | 'board' | 'list';

export enum DisabledReason {
  LoginRequired = 'login_required',
  NoProducts = 'no_products',
  LimitExceeded = 'limit_exceeded',
  MultipleGuestsRequested = 'multiple_guests_requested',
  UnidentifiedSeller = 'unidentified_seller',
  MultipleReservationsRequested = 'multiple_reservadtions_requested',
}

export interface PaymentMethodCreditCard {
  method: 'credit_card';
  price: string;
  available: boolean;
  disabled_reasons?: DisabledReason[];
}

export type PaymentMethod = PaymentMethodCreditCard;

export type CreditCardError =
  | { type: 'invalid_number'; field: 'number' }
  | { type: 'invalid_expiry_month'; field: 'month' }
  | { type: 'invalid_expiry_year'; field: 'year' }
  | { type: 'invalid_cvc'; field: 'cvc' }
  | { type: 'incorrect_number'; field: 'number' }
  | { type: 'expired_card'; field: 'exp' }
  | { type: 'incorrect_cvc'; field: 'cvc' }
  | { type: 'card_declined'; field: 'card' }
  | { type: 'missing'; field: 'card' }
  | { type: 'processing_error'; field: 'card' }
  | { type: 'script_load_failed'; field: 'card' };

export interface SettingPayment {
  errors?: { creditCard: CreditCardError };
}

export enum ErrorType {
  Blank = 'blank',
  Invalid = 'invalid',
}

export interface CustomerField {
  id: number;
  inputType: string;
  validationType: string;
  label: string;
  valueOptions?: string[];
  description: string;
  required: boolean;
  defaultValue?: string;
  errors?: ErrorType[];
}

export interface SettingCustomer {
  customerFields?: CustomerField[];
}

export enum SchemeType {
  Subscription = 'subscription',
  TicketBook = 'ticket_book',
}

export type Settings = SettingsSubscription | SettingsTicketBook;

export interface SettingsSubscription {
  schemeType: SchemeType.Subscription;
  customer: SettingCustomer;
  payment?: SettingPayment;
  confirmation: SettingConfirmation;
  completion: SettingCompletion;
}

export interface SettingsTicketBook {
  schemeType: SchemeType.TicketBook;
  customer: SettingCustomer;
  payment?: SettingPayment;
  confirmation: SettingConfirmation;
  completion: SettingCompletion;
}

export interface State {
  settings?: Settings;
  step?: number;
  postable?: boolean;
  publicId?: string;
  productId?: string;
  merchantProduct?: MerchantProduct;
  renew?: boolean;
}

export interface MerchantProduct {
  publicId: string;
  name: string;
  schemeType: string;
  price: number;
  image: Image;
  description?: string;
  ticketCount?: number;
  expiration?: number;
}

interface UseAnotherCard {
  method: 'credit_card';
  restore_credit_card: false;
  ctok?: string;
}

interface UseUsedCard {
  method: 'credit_card';
  restore_credit_card: true;
}

interface UseTotallyNew {
  method: 'credit_card';
  ctok?: string;
}

export type PurchasingRequestPaymentMethod =
  | UseAnotherCard
  | UseUsedCard
  | UseTotallyNew;

export interface PurchasingRequestCustomerCustomFieldsAttribute {
  binder_id: string;
  value: string;
}

export interface PurchasingRequestCustomerCustomFields {
  customer_custom_field_responses_attributes: PurchasingRequestCustomerCustomFieldsAttribute[];
}

export interface PurchasingRequestInput {
  customer_custom_fields?: PurchasingRequestCustomerCustomFields;
  payment_method?: PurchasingRequestPaymentMethod;
}

export interface PurchasingRequestData {
  product_id: string;
  input: PurchasingRequestInput;
}

export interface PurchasingCustomerCustomFieldsError {
  [id: string]: ErrorType[];
}

export enum PurchasingPaymentMethodError {
  StripeErrorOccurred = 'stripe_error_occurred',
}

export interface PurchasingErrors {
  customer_custom_fields: PurchasingCustomerCustomFieldsError;
  payment_method: PurchasingPaymentMethodError;
}

export interface PurchasingResponseData {
  success: boolean;
  customer?: ICustomer.CustomerResponse;
  verified_user?: boolean;
  errors?: PurchasingErrors;
}
